import { zodResolver } from '@hookform/resolvers/zod';
import { EmbeddedMessage, MessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import { BLAST, TRIGGERED } from 'constants/templateTypes';
import { selectIsEmbeddedMessagingCoreFeatureFlagOn } from 'core/userContext';
import { useCreateCampaignContext } from 'modules/campaignIndex/campaignFolders/contexts/CreateCampaignContext';
import { getIsEmbeddedMessage } from 'modules/campaignIndex/utils';
import { getIsMediumWithDestination } from 'modules/campaigns/editor/utils';
import { AllMessageMedium, CampaignType } from 'modules/domains/campaign';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import { CAMPAIGN_DATE_FORMAT, getNewDate } from 'utils/date';
import { z } from 'zod';

const DESTINATION_CATEGORY_IDS = [-2, -1, 0];

export type CreateCampaignFormProviderProps = {
  children: React.ReactNode;
};

type SchemaExternalDependencies = {
  messageMedium: MessageMedium;
  isEmbeddedMessagingEnabled?: boolean;
};

const BaseCampaignSchema = z.object({
  campaignType: z.string(),
  labels: z.string().array().optional(),
  name: z.string().min(1, 'Name is required'),
});

const EmbeddedExclusiveSchema = z.object({
  placement: z.coerce.number({
    /*
     * Value is set as a string because of constraints with
     * BasicFilterSelect component used for placements when
     * there are more than 5 options. This value is then
     * coerced to NaN if no placement selection is made so
     * it will catch on invalid type error instead of required
     */
    invalid_type_error: 'Placement required to create campaign',
  }),
});

const DestinationSchema = z.object({
  mobileAppIds: z.optional(
    z
      .number()
      .array()
      .refine((ids) => {
        // all destinations selected
        if (ids.length === 0) {
          return true;
        }

        // one entire destination category selected
        if (ids.length === 1 && DESTINATION_CATEGORY_IDS.includes(ids[0])) {
          return true;
        }

        // custom destination selection
        if (ids.length >= 1 && ids.every((id) => id > 0)) {
          return true;
        }

        return false;
      }),
  ),
});

const getSchema = ({
  messageMedium,
  isEmbeddedMessagingEnabled = false,
}: SchemaExternalDependencies) => {
  if (getIsMediumWithDestination(messageMedium)) {
    return BaseCampaignSchema.merge(DestinationSchema);
  }
  if (getIsEmbeddedMessage(messageMedium) && isEmbeddedMessagingEnabled) {
    return BaseCampaignSchema.merge(EmbeddedExclusiveSchema);
  }

  return BaseCampaignSchema;
};

// Matches new journey name formatting
const defaultCampaignName = `Campaign created on ${getNewDate().format(CAMPAIGN_DATE_FORMAT)}`;

const getDefaultValues = (
  messageMedium: AllMessageMedium,
  hasApprovedBusinessTemplates: boolean,
) => {
  const campaignType = match(messageMedium)
    .with(EmbeddedMessage, () => CampaignType.Eligibility)
    .with(WhatsAppMedium.WhatsApp, () => (hasApprovedBusinessTemplates ? BLAST : TRIGGERED))
    .otherwise(() => BLAST);

  return {
    campaignType,
    labels: [],
    name: defaultCampaignName,
  };
};

export const CreateCampaignFormProvider = ({ children }: CreateCampaignFormProviderProps) => {
  const { hasApprovedBusinessTemplates, messageMedium } = useCreateCampaignContext();

  const isEmbeddedMessagingEnabled = useSelector(selectIsEmbeddedMessagingCoreFeatureFlagOn);

  const schema = getSchema({ messageMedium, isEmbeddedMessagingEnabled });
  const isEmbeddedMessage = getIsEmbeddedMessage(messageMedium);
  const defaultValues = getDefaultValues(messageMedium, hasApprovedBusinessTemplates);

  type FormSchemaType = Required<z.infer<typeof schema>>;
  const methods = useForm<FormSchemaType>({
    defaultValues,
    mode: isEmbeddedMessage ? 'onSubmit' : 'onChange',
    resolver: zodResolver(schema),
    shouldUnregister: true,
  });

  return <FormProvider {...methods}>{children}</FormProvider>;
};
